import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Box, IconButton, Paper, Tooltip } from "@material-ui/core"
import CloseIcon from "@material-ui/icons/Close"
import DragIndicatorIcon from "@material-ui/icons/DragIndicator"
import PinDrop from "@material-ui/icons/PinDrop"
import { useMemo, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { GoogleMapsViewer, MapDrawing } from ".."
import { useStateMap } from "../../../hooks"
import type { MapDetailDto } from "../../../models"
import type { AppState, AppThunkDispatch, LegendItem, MapAction, MapActions, Project } from "../../../redux"
import { legendStringToLegendItems } from "../../../redux"
import { convertToLatLng, deviceHasTouchEnabled, extractPrefix, getMapTitle } from "../../../utils"
import { ErrorBoundary, Header5 } from "../../atoms"
import { MapViewer, MapViewerControls } from "../../organisms"

import "./comparison-project.styles.scss"
import { useTranslation } from "react-i18next"

export interface IComparisonProjectStateProps {
  getDispatch: () => AppThunkDispatch<MapAction>
}

export interface IComparisonProjectOwnProps {
  project: Project
  prefix?: string
  index: number
  canRemove: boolean
  hideHeader?: boolean
  hideControls?: boolean
  onProjectRemove?: (index: number) => void
  onShowPreview?: (project: Project) => void
}

function ComparisonProjectView(props: IComparisonProjectOwnProps & IComparisonProjectStateProps): ReactElement {
  const { project, prefix, index, canRemove, hideHeader, hideControls, onProjectRemove, getDispatch, onShowPreview } =
    props

  const { t } = useTranslation("comparison")

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `${index}` })

  const [isGoogleMapsActive, setIsGoogleMapsActive] = useState<boolean>(false)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition as string
  }

  const map: MapDetailDto | undefined = useStateMap(project.metaDataId, getDispatch())

  const mapTitle: string = useMemo<string>(() => {
    const showMapTitle: boolean = project.id === undefined || !project.title
    return (showMapTitle && map ? getMapTitle(map) : project.title) as string
  }, [map, project])

  const legendItems: LegendItem[] | undefined = useMemo<LegendItem[] | undefined>(() => {
    if (!map) return undefined
    return map.legend ? legendStringToLegendItems(map.legend) : undefined
  }, [map])

  function onRemoveClicked(): void {
    if (!onProjectRemove) return

    onProjectRemove(index)
  }

  function toggleGoogleMaps() {
    setIsGoogleMapsActive(!isGoogleMapsActive)
  }

  function handleDoubleClick(): void {
    if (onShowPreview) {
      onShowPreview(project)
    }
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={`comparison-project-drag-container ${isDragging ? "dragging" : ""}`}
      ref={setNodeRef}
      style={style}
    >
      <Paper elevation={0} className={`comparison-project ${hideHeader ? "hidden-header" : ""}`}>
        {map && (
          <>
            {!hideHeader && (
              <Box className="comparison-project-header">
                <Header5>{mapTitle}</Header5>
                <Box className="comparison-project-actions">
                  {!hideControls && (
                    <>
                      <Tooltip title={t("controls.googleMaps")} placement="top">
                        <IconButton onClick={() => toggleGoogleMaps()}>
                          <PinDrop />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("controls.drag")} placement="top">
                        <IconButton {...attributes} {...listeners}>
                          <DragIndicatorIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {canRemove === true && (
                    <Tooltip title={t("controls.delete")} placement="top">
                      <IconButton onClick={onRemoveClicked}>
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}
            <ErrorBoundary>
              <MapViewer
                hideMap={isGoogleMapsActive}
                prefix={prefix}
                mapId={`${index}-${map.id}`}
                casperId={map.casperId as string}
                corner={convertToLatLng(map.corner)}
                oppositeCorner={convertToLatLng(map.oppositeCorner)}
                projection={map.projection}
              >
                <MapDrawing
                  drawings={project.drawings}
                  attachments={[]}
                  showDrawings={true && !isGoogleMapsActive}
                  removePinLocation={() => {}}
                />
                <Box
                  className={`navigation-controls ${
                    deviceHasTouchEnabled() ? "disable-hover" : ""
                  } ${isGoogleMapsActive ? "hide" : ""}`}
                >
                  <MapViewerControls
                    prefix={
                      prefix
                        ? `${prefix}-${extractPrefix(map.casperId as string)}`
                        : extractPrefix(map.casperId as string)
                    }
                    mapId={`${index}-${map.id}`}
                    corner={convertToLatLng(map.corner)}
                    oppositeCorner={convertToLatLng(map.oppositeCorner)}
                    legendItems={legendItems}
                    legendUrl={map.legendUrl}
                    options={{
                      drawingControls: false,
                      mapActions: false
                    }}
                  />
                  <GoogleMapsViewer
                    mapId={`${index}-${map.id}`}
                    hidden={!isGoogleMapsActive}
                    corner={convertToLatLng(map.corner)}
                    oppositeCorner={convertToLatLng(map.oppositeCorner)}
                  />
                </Box>
              </MapViewer>
            </ErrorBoundary>
          </>
        )}
      </Paper>
    </div>
  )
}

function mapStateToProps(state: AppState, ownProps: IComparisonProjectOwnProps) {
  return {
    map: state.maps.loadedMaps[ownProps.project.metaDataId]
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapActions>) {
  return {
    getDispatch: () => dispatch
  }
}

export const ComparisonProject = connect(mapStateToProps, mapDispatchToProps)(ComparisonProjectView)
